import React, { useState } from 'react';
import { Field, Formik, Form, ErrorMessage } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { CardNumberElement, CardExpiryElement, CardCvcElement, Elements, useElements } from '@stripe/react-stripe-js';
import { ErrorText, H4, LabelLarge } from '@styled';
import useStripeInstances from '@hooks/useStripeInstances';

const CreditCardFormInner = ({ card, isSubmitting, onSave, onCancel }) => {
    const { stripePromises } = useStripeInstances();
    const [stripeErrors, setStripeErrors] = useState({});

    // -----------------------------
    // Inner Component
    // -----------------------------

    const CreditCardFields = ({ values, setTouched, validateForm }) => {
        const elements = useElements();

        const validateStripeFields = () => {
            const cardNumberElement = elements.getElement(CardNumberElement);
            const cardCvcElement = elements.getElement(CardCvcElement);
            const cardExpiryElement = elements.getElement(CardExpiryElement);
            const errors = {};

            if (cardNumberElement._empty) {
                errors.cardNumber = 'Card Number is required.';
            } else if (cardNumberElement._invalid) {
                errors.cardNumber = 'Card Number is invalid.';
            }

            if (cardCvcElement._empty) {
                errors.cardCvc = 'CVV is required.';
            } else if (cardCvcElement._invalid) {
                errors.cardCvc = 'CVV is invalid.';
            }

            if (cardExpiryElement._empty) {
                errors.cardExpiry = 'Expiration Date is required.';
            } else if (cardExpiryElement._invalid) {
                errors.cardExpiry = 'Expiration Date is invalid.';
            }

            if (Object.keys(errors).length > 0) {
                setStripeErrors(errors);
            }

            return errors;
        };

        return (
            <>
                {!card.id && (
                    <>
                        <H4 className="mb-1rem">Add New Payment Method</H4>

                        <div className="form-group">
                            <label className="form-control-label">
                                <LabelLarge>Card Number</LabelLarge>
                            </label>

                            <CardNumberElement />

                            {stripeErrors.cardNumber && (
                                <p className="m-0" style={{ fontSize: '1rem' }}>
                                    <ErrorText>{stripeErrors.cardNumber}</ErrorText>
                                </p>
                            )}
                        </div>

                        <div className="form-group">
                            <label className="form-control-label">
                                <LabelLarge>CVV</LabelLarge>
                            </label>

                            <CardCvcElement />

                            {stripeErrors.cardCvc && (
                                <p className="m-0" style={{ fontSize: '1rem' }}>
                                    <ErrorText>{stripeErrors.cardCvc}</ErrorText>
                                </p>
                            )}
                        </div>

                        <div className="form-group">
                            <label className="form-control-label">
                                <LabelLarge>Expiration Date</LabelLarge>
                            </label>

                            <CardExpiryElement />

                            {stripeErrors.cardExpiry && (
                                <p className="m-0" style={{ fontSize: '1rem' }}>
                                    <ErrorText>{stripeErrors.cardExpiry}</ErrorText>
                                </p>
                            )}
                        </div>
                    </>
                )}

                {!!card.id && (
                    <h3>
                        Update {card.type} ending in {card.last4}
                    </h3>
                )}

                <div className="form-group">
                    <label className="form-control-label">
                        <LabelLarge>Name on Card</LabelLarge>
                    </label>
                    <Field className="form-control p-3" maxLength="100" name="name" required type="text" />

                    <ErrorText>
                        <ErrorMessage name="name" />
                    </ErrorText>
                </div>

                <div className="form-group">
                    <label className="form-control-label">
                        <LabelLarge>Billing Zip</LabelLarge>
                    </label>
                    <Field className="form-control p-3" maxLength="12" name="address_zip" required type="text" />

                    <ErrorText>
                        <ErrorMessage name="address_zip" />
                    </ErrorText>
                </div>

                <div className="mt-4">
                    <div id="credit-card-update-buttons">
                        <button
                            type="button"
                            className="btn btn-primary"
                            disabled={isSubmitting}
                            onClick={async () => {
                                const stripeValidationErrors = !card.id ? validateStripeFields() : {};
                                const validationErrors = {
                                    ...stripeValidationErrors,
                                    ...(await validateForm()),
                                };

                                if (Object.keys(validationErrors).length === 0) {
                                    if (!card.id) {
                                        onSave({
                                            cardNumberElement: elements.getElement(CardNumberElement),
                                            cardValues: values,
                                        });
                                    }
                                } else {
                                    setTouched({
                                        name: true,
                                        address_zip: true,
                                    });
                                }
                            }}
                        >
                            Save
                        </button>

                        <button
                            type="button"
                            className="btn btn-primary ml-2"
                            disabled={isSubmitting}
                            onClick={() => {
                                onCancel();
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </div>

                {isSubmitting && <p className="mt-2">Submitting...</p>}
            </>
        );
    };

    CreditCardFields.propTypes = {
        values: PropTypes.object,
        setTouched: PropTypes.func.isRequired,
        validateForm: PropTypes.func.isRequired,
    };

    // -----------------------------
    // Render wrapper
    // -----------------------------

    const initialValues = {
        name: card.name,
        address_zip: card.address_zip,
    };

    const validationSchema = Yup.object().shape({
        name: Yup.string().required('Name on Card is required.'),
        address_zip: Yup.string().required('Billing Zip is required.'),
    });

    return (
        <Formik initialValues={initialValues} validationSchema={validationSchema}>
            {stripeProps => (
                <Form id="credit-card-form">
                    <Elements stripe={stripePromises.US}>
                        <CreditCardFields {...stripeProps} />
                    </Elements>
                </Form>
            )}
        </Formik>
    );
};

const CreditCardFormWrapper = ({ card, isSubmitting, onSave, onCancel }) => (
    <CreditCardFormInner card={card} isSubmitting={isSubmitting} onSave={onSave} onCancel={onCancel} />
);

CreditCardFormInner.propTypes = {
    card: PropTypes.object,
    isSubmitting: PropTypes.bool,
    onSave: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
};

CreditCardFormWrapper.propTypes = {
    card: PropTypes.object,
    isSubmitting: PropTypes.bool,
    onSave: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
};

export default CreditCardFormWrapper;
